import { useState } from "react";
import {
	SafetyControlSurveyLineApi,
	SafetyControlSurveyLineApi_SurveyLineWithQuestion,
} from "src/api/generated/erp/safetyControlSurvey/api/safetyControlSurveyLineApi.ts";
import { AsyncFetchRender } from "src/components/common/async/AsyncFetchRender.tsx";
import { VerticalBox } from "src/components/common/box/VerticalBox.tsx";
import { HorizontalBox } from "src/components/common/box/HorizontalBox.tsx";
import { AsyncButton } from "src/components/common/buttons/AsyncButton.tsx";
import { faCheck, faMinus, faPlus } from "@fortawesome/pro-regular-svg-icons";
import i18n from "i18next";
import { Box, IconButton, Paper, Typography } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useGenericDialog } from "src/components/common/dialogs/GenericDialogContext.ts";
import { SafetyControlSurveyNoteForm } from "./SafetyControlSurveyNoteForm.tsx";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";

export interface FillSafetyControlSurveyViewProps {
	safetyControlSurveyId: number;
	onClose: () => void;
}

export const FillSafetyControlSurveyView = ({ safetyControlSurveyId, onClose }: FillSafetyControlSurveyViewProps) => {
	return (
		<AsyncFetchRender
			fetch={() => SafetyControlSurveyLineApi.getLines({ safetyControlSurveyId })}
			content={(lines, refresh) => (
				<FillSafetyControlSurveyContent
					safetyControlSurveyId={safetyControlSurveyId}
					lines={lines}
					onRefresh={refresh}
					onClose={onClose}
				/>
			)}
		/>
	);
};

interface FillSafetyControlSurveyContentProps {
	safetyControlSurveyId: number;
	lines: SafetyControlSurveyLineApi_SurveyLineWithQuestion[];
	onRefresh: () => void;
	onClose: () => void;
}

const FillSafetyControlSurveyContent = ({
	safetyControlSurveyId,
	lines,
	onRefresh,
	onClose,
}: FillSafetyControlSurveyContentProps) => {
	const { openDialog } = useGenericDialog();
	// Group lines by group name
	const groupedLines = lines.reduce(
		(acc, line) => {
			const groupName = line.groupName ?? "";
			if (!acc[groupName]) {
				acc[groupName] = [];
			}
			acc[groupName].push(line);
			return acc;
		},
		{} as Record<string, SafetyControlSurveyLineApi_SurveyLineWithQuestion[]>,
	);

	return (
		<VerticalBox
			sx={{
				flex: 1,
				alignSelf: "stretch",
				overflow: "auto",
				p: 2,
				gap: 2,
			}}
		>
			{Object.entries(groupedLines).map(([groupName, groupLines]) => (
				<Paper
					key={groupName}
					sx={{
						flex: 1,
						p: 2,
					}}
				>
					{groupName === "" ? null : (
						<Typography variant="h6" sx={{ mb: 2 }}>
							{groupName}
						</Typography>
					)}
					<VerticalBox sx={{ gap: 1 }}>
						{groupLines.map((line) => (
							<SurveyLineRow key={line.safetyControlSurveyLineId} line={line} onRefresh={onRefresh} />
						))}
					</VerticalBox>
				</Paper>
			))}
			<HorizontalBox sx={{ justifyContent: "flex-end" }}>
				<AsyncButton
					label={i18n.t("add_note")}
					icon={faPlus}
					onClick={() => {
						openDialog(({ closeDialog }) => ({
							title: i18n.t("add_note"),
							size: "md",
							agentContext: {
								key: defineAgentUiContextKey("erp.safetyControl.surveyNote.formDialog"),
								data: {
									safetyControlSurveyId,
									safetyControlSurveyNoteId: null,
								},
								pathSegments: [i18n.t("add_note")],
							},
							content: (
								<SafetyControlSurveyNoteForm
									note={undefined}
									safetyControlSurveyId={safetyControlSurveyId}
									onCompleted={async () => {
										await closeDialog();
									}}
									onFormEdited={() => {}}
								/>
							),
						}));
					}}
				/>
				<AsyncButton label={i18n.t("save")} icon={faCheck} onClick={onClose} />
			</HorizontalBox>
		</VerticalBox>
	);
};

interface SurveyLineRowProps {
	line: SafetyControlSurveyLineApi_SurveyLineWithQuestion;
	onRefresh: () => void;
}

const SurveyLineRow = ({ line, onRefresh }: SurveyLineRowProps) => {
	const [isUpdating, setIsUpdating] = useState(false);

	async function handleIncrement(answerType: "RIGHT" | "WRONG") {
		setIsUpdating(true);
		try {
			await SafetyControlSurveyLineApi.incrementCount({
				safetyControlSurveyLineId: line.safetyControlSurveyLineId,
				answerType,
			});
			onRefresh();
		} finally {
			setIsUpdating(false);
		}
	}

	async function handleDecrement(answerType: "RIGHT" | "WRONG") {
		setIsUpdating(true);
		try {
			await SafetyControlSurveyLineApi.decrementCount({
				safetyControlSurveyLineId: line.safetyControlSurveyLineId,
				answerType,
			});
			onRefresh();
		} finally {
			setIsUpdating(false);
		}
	}

	return (
		<HorizontalBox sx={{ alignItems: "center", gap: 2, py: 1 }}>
			<Typography sx={{ flex: 1 }}>{line.questionText}</Typography>
			<HorizontalBox sx={{ alignItems: "center", gap: 2 }}>
				<CounterControl
					label={i18n.t("right")}
					count={line.rightAnswerCount}
					color="success"
					onIncrement={() => handleIncrement("RIGHT")}
					onDecrement={() => handleDecrement("RIGHT")}
					disabled={isUpdating}
				/>
				<CounterControl
					label={i18n.t("wrong")}
					count={line.wrongAnswerCount}
					color="error"
					onIncrement={() => handleIncrement("WRONG")}
					onDecrement={() => handleDecrement("WRONG")}
					disabled={isUpdating}
				/>
			</HorizontalBox>
		</HorizontalBox>
	);
};

interface CounterControlProps {
	label: string;
	count: number;
	color: "success" | "error";
	onIncrement: () => void;
	onDecrement: () => void;
	disabled: boolean;
}

const CounterControl = ({ label, count, color, onIncrement, onDecrement, disabled }: CounterControlProps) => {
	return (
		<HorizontalBox sx={{ alignItems: "center", gap: 0.5 }}>
			<Typography variant="caption" color={`${color}.main`} sx={{ minWidth: 40 }}>
				{label}
			</Typography>
			<IconButton size="small" onClick={onDecrement} disabled={disabled || count === 0} color={color}>
				<FontAwesomeIcon icon={faMinus} />
			</IconButton>
			<Box sx={{ minWidth: 30, textAlign: "center" }}>
				<Typography variant="body2" color={`${color}.main`}>
					{count}
				</Typography>
			</Box>
			<IconButton size="small" onClick={onIncrement} disabled={disabled} color={color}>
				<FontAwesomeIcon icon={faPlus} />
			</IconButton>
		</HorizontalBox>
	);
};
